import React from 'react';
import { Navigate, Route } from 'react-router-dom';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import { TechRadarPage } from '@backstage-community/plugin-tech-radar';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { apis, ktnIdsOIDCAuthApiRef } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';

import {
  AlertDisplay,
  OAuthRequestDialog,
  SignInPage,
} from '@backstage/core-components';
import { createApp } from '@backstage/app-defaults';
import { FlatRoutes, AppRouter } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { RequirePermission } from '@backstage/plugin-permission-react';

import LightIcon from '@mui/icons-material/WbSunny';
import { ScaffolderFieldExtensions } from '@backstage/plugin-scaffolder-react';
import { UnifiedThemeProvider } from '@backstage/theme';
import { backstageTheme } from './theme';
import { CustomProviderSettings } from './components/userSettings/CustomProviderSettings';

import { RepoUrlPickerExtension } from './components/repoUrlPicker';
import { ExistingRepoPickerExtension } from './components/existingRepoPicker';
import {
  CreateDatabasePage,
  ListPageRequests,
  CreateServiceAccountsPage,
  AddDatabasePermissionsPage,
} from '@internal/plugin-alm-services';

const app = createApp({
  apis,
  components: {
    SignInPage: props => (
      <SignInPage
        {...props}
        providers={[
          {
            id: 'ktnids',
            title: 'Katoen Natie Identity Server',
            message: 'Login using your Katoen Natie account',
            apiRef: ktnIdsOIDCAuthApiRef,
          },
        ]}
      />
    ),
  },
  themes: [
    {
      id: 'my-theme',
      title: 'My Custom Theme',
      variant: 'light',
      icon: <LightIcon />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={backstageTheme} children={children} />
      ),
    },
  ],
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
});

const routes = (
  <FlatRoutes>
    <Route path="/" element={<Navigate to="catalog" />} />
    <Route path="/catalog" element={<CatalogIndexPage />} />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />} />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        <ReportIssue />
      </TechDocsAddons>
    </Route>
    <Route
      path="/create"
      element={
        <ScaffolderPage
          groups={[
            {
              title: 'ALM',
              filter: entity =>
                entity?.metadata?.tags?.includes('alm') ?? false,
            },
            {
              title: '.NET',
              filter: entity =>
                entity?.metadata?.tags?.includes('dotnet') ?? false,
            },
            {
              title: 'React',
              filter: entity =>
                entity?.metadata?.tags?.includes('react') ?? false,
            },
          ]}
        />
      }
    >
      <ScaffolderFieldExtensions>
        <RepoUrlPickerExtension />
        <ExistingRepoPickerExtension />
      </ScaffolderFieldExtensions>
    </Route>
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route
      path="/tech-radar"
      element={<TechRadarPage width={1500} height={800} />}
    />
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route
      path="/settings"
      element={
        <UserSettingsPage providerSettings={<CustomProviderSettings />} />
      }
    />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route path="/alm-services/requests" element={<ListPageRequests />} />
    <Route
      path="/alm-services/create-databases"
      element={<CreateDatabasePage />}
    />
    <Route
      path="/alm-services/create-service-accounts"
      element={<CreateServiceAccountsPage />}
    />
    <Route
      path="/alm-services/add-database-permissions"
      element={<AddDatabasePermissionsPage />}
    />
  </FlatRoutes>
);

export default app.createRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <AppRouter>
      <Root>{routes}</Root>
    </AppRouter>
  </>,
);
